import { useQuery, useMutation, useQueryClient } from 'react-query';
import api from '../../../lib/api';
import toast from 'react-hot-toast';

export const PLATFORM_ADMINS_QUERY_KEY = 'super-admin-admins';

function serverErrorMessage(err, fallback) {
  const data = err?.response?.data;
  if (typeof data?.error === 'string' && data.error) return data.error;
  if (Array.isArray(data?.errors) && data.errors.length > 0) {
    const first = data.errors[0];
    return first?.msg || first?.message || fallback;
  }
  return fallback;
}

/**
 * Platform admin list for the super-admin screen.
 * Caller must gate with `enabled`.
 */
export function usePlatformAdminsList({ enabled } = {}) {
  const { data, isLoading, error } = useQuery(
    [PLATFORM_ADMINS_QUERY_KEY],
    () => api.get('/super-admin/admins').then((res) => res.data),
    { enabled: !!enabled }
  );

  return { data, isLoading, error };
}

/**
 * Grant platform admin access by email. Invalidates the list on success.
 */
export function useGrantPlatformAdmin({ onSuccess } = {}) {
  const queryClient = useQueryClient();

  return useMutation(
    (payload) => api.post('/super-admin/admins', payload),
    {
      onSuccess: (res) => {
        queryClient.invalidateQueries([PLATFORM_ADMINS_QUERY_KEY]);
        toast.success('Platform admin access granted');
        onSuccess?.(res);
      },
      onError: (err) => {
        toast.error(serverErrorMessage(err, 'Failed to grant platform admin access'));
      },
    }
  );
}

/**
 * Revoke platform admin access. Invalidates the list on success.
 */
export function useRevokePlatformAdmin() {
  const queryClient = useQueryClient();

  return useMutation(
    (userId) => api.delete(`/super-admin/admins/${userId}`),
    {
      onSuccess: () => {
        queryClient.invalidateQueries([PLATFORM_ADMINS_QUERY_KEY]);
        toast.success('Platform admin access revoked');
      },
      onError: (err) => {
        toast.error(serverErrorMessage(err, 'Failed to revoke platform admin access'));
      },
    }
  );
}
